import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import classNames from "classnames";
import {ReactElement, ReactNode, useState} from "react";
import CopyToClipboard from "react-copy-to-clipboard";
import {FaEllipsis} from "react-icons/fa6";
import {Link} from "react-router";

import Button from "@/components/button/Button.tsx";
import {ButtonSize, ButtonVariant} from "@/components/button/Button.types.ts";
import {Icon} from "@/components/icon/Icon.tsx";
import {DropdownDivider} from "@/components/quickActionsBar/DropdownDivider.tsx";
import {Tooltip} from "@/components/toolTip/Tooltip.tsx";

import styles from "./DropdownAction.module.scss";

export type DropdownItem =
  | ReactElement<typeof DropDownLink>
  | ReactElement<typeof DropDownReactLink>
  | ReactElement<typeof DropdownActionItem>
  | ReactElement<typeof DropdownDivider>
  | ReactElement<typeof CopyToClipboard>;

interface Props {
  id: string;
  items: DropdownItem[];
  moreActionsText?: string | null;
  buttonContent?: ReactNode;
  buttonVariant?: ButtonVariant;
  buttonSize?: ButtonSize;
  dropdownMenuClassName?: string;
}

export const DropdownAction = ({
  id,
  moreActionsText,
  buttonContent,
  buttonVariant,
  buttonSize,
  dropdownMenuClassName,
  items,
}: Props) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const {refs, floatingStyles, context} = useFloating({
    open: isDropdownOpen,
    onOpenChange: setIsDropdownOpen,
    placement: "bottom-end",
    whileElementsMounted: autoUpdate,
    middleware: [offset({crossAxis: -10}), flip(), shift({padding: 40})],
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, {role: "menu"});

  const {getReferenceProps, getFloatingProps} = useInteractions([click, dismiss, role]);

  const triggerProps = {
    type: "button" as const,
    id: `dropdownMenuButton-${id}`,
    ref: refs.setReference,
    disabled: items.length === 0,
    "aria-haspopup": true,
    "aria-expanded": isDropdownOpen,
    ...getReferenceProps(),
  };

  const triggerContent = (
    <div className={"d-flex align-items-center gap-2"}>{buttonContent ?? <Icon icon={FaEllipsis} />}</div>
  );

  const trigger = buttonVariant ? (
    <Button variant={buttonVariant} size={buttonSize} {...triggerProps}>
      {triggerContent}
    </Button>
  ) : (
    <button className="btn border-0 text-dark btn-outline-light btn-sm" {...triggerProps}>
      {triggerContent}
    </button>
  );

  return (
    <>
      <span className={classNames("dropdown d-flex", styles.dropdown)}>
        {moreActionsText === null ? (
          trigger
        ) : (
          <Tooltip
            tooltipContent={moreActionsText !== undefined ? moreActionsText : "More Actions"}
            open={isDropdownOpen ? false : undefined}
          >
            {trigger}
          </Tooltip>
        )}
      </span>

      {isDropdownOpen && (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false}>
            <ul
              ref={refs.setFloating}
              style={floatingStyles}
              className={classNames("dropdown-menu show font-14 me-2", dropdownMenuClassName)}
              aria-labelledby={`dropdownMenuButton-${id}`}
              {...getFloatingProps()}
            >
              {items.map((item, idx) => (
                <li key={`${id}-quick-action-${idx}`}>{item}</li>
              ))}
            </ul>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
};

interface DropDownLinkProps extends Partial<HTMLAnchorElement> {
  variant?: ButtonVariant;
  className?: string;
}

export const DropDownLink = ({text, href, target, rel, variant = "dropdownMenuItem", className}: DropDownLinkProps) => (
  <Button
    variant={variant}
    className={classNames("text-nowrap", className)}
    as={"a"}
    href={href}
    target={target}
    rel={rel}
  >
    {text}
  </Button>
);

interface DropDownReactLinkProps {
  text: ReactNode;
  to: string;
  // eslint-disable-next-line typescript/no-explicit-any
  state?: any;
  // a unique id used by e2e tests, https://playwright.dev/docs/locators#locate-by-test-id
  dataTestId?: string;
}

export const DropDownReactLink = ({text, to, state, dataTestId}: DropDownReactLinkProps) => (
  <Button variant="dropdownMenuItem" className="text-nowrap" as={Link} to={to} data-testid={dataTestId} state={state}>
    {text}
  </Button>
);

interface DropdownActionItemProps {
  text: ReactNode;
  onClick: () => void;
  loading?: boolean;
}

export const DropdownActionItem = ({text, onClick, loading}: DropdownActionItemProps) => (
  <Button
    variant="dropdownMenuItem"
    className={classNames("text-nowrap", {"cursor-wait": loading})}
    onClick={onClick}
    disabled={loading}
  >
    {text}
  </Button>
);
